import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DollarSign, TrendingUp, Store, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function FinancialReports() {
  const { api } = useAuth();
  const [reports, setReports] = useState(null);
  const [restaurantReports, setRestaurantReports] = useState([]);
  const [salesReports, setSalesReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [financial, restaurants, sales] = await Promise.all([
        api.get('/reports/financial'),
        api.get('/reports/restaurants'),
        api.get('/reports/sales-commission'),
      ]);

      setReports(financial.data);
      setRestaurantReports(restaurants.data);
      setSalesReports(sales.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Financial Reports</h1>
        <div className="grid md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
              <div className="h-6 bg-slate-200 rounded w-1/2 mb-4"></div>
              <div className="h-10 bg-slate-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Financial Reports</h1>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900">£{reports?.total_revenue?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Platform Commission</p>
                <p className="text-2xl font-bold text-slate-900">£{reports?.total_commission?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Orders</p>
                <p className="text-2xl font-bold text-slate-900">{reports?.total_orders || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <Store className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Sales Commission</p>
                <p className="text-2xl font-bold text-slate-900">£{salesReports?.total_commission?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Restaurant Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Restaurant Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {restaurantReports.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Restaurant</th>
                    <th>Total Orders</th>
                    <th>Revenue</th>
                    <th>Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {restaurantReports.map((report) => (
                    <tr key={report.restaurant_id}>
                      <td className="font-medium text-slate-900">{report.restaurant_name}</td>
                      <td>{report.total_orders}</td>
                      <td>£{report.total_revenue.toFixed(2)}</td>
                      <td>£{report.total_commission.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No restaurant data available</p>
          )}
        </CardContent>
      </Card>

      {/* Sales Commission Details */}
      <Card>
        <CardHeader>
          <CardTitle>Sales Commission Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500">Total Commission</p>
              <p className="text-xl font-bold text-slate-900">£{salesReports?.total_commission?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500">Pending Payout</p>
              <p className="text-xl font-bold text-orange-500">£{salesReports?.pending_commission?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500">Paid Out</p>
              <p className="text-xl font-bold text-green-500">£{salesReports?.paid_commission?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
